import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";

export const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: -72, duration: 1.4 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const { lang, setLang, t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isB2bHome = pathname === "/";
  const contactLabel = isB2bHome ? (lang === "ru" ? "Коммерческий запрос" : "Commercial enquiry") : t("nav.cta");
  const directionLinks = lang === "ru"
    ? [
        { href: "/gold-brokerage-dubai", label: "Золото" },
        { href: "/petroleum-brokerage-dubai", label: "Нефтепродукты" },
        { href: "/sblc-escrow-gold-brokerage-dubai", label: "SBLC и escrow" },
        { href: "/kyc-aml-commercial-brokerage", label: "KYC / AML" },
      ]
    : [
        { href: "/gold-brokerage-dubai", label: "Gold" },
        { href: "/petroleum-brokerage-dubai", label: "Petroleum" },
        { href: "/sblc-escrow-gold-brokerage-dubai", label: "SBLC & escrow" },
        { href: "/kyc-aml-commercial-brokerage", label: "KYC / AML" },
      ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    const localId = id === "contact" && document.getElementById("sblc-contact") ? "sblc-contact" : id;
    if (document.getElementById(localId)) {
      scrollToId(localId);
      return;
    }
    navigate(`/#${id}`);
  };

  const goHomeOrTop = () => {
    if (document.getElementById("top")) {
      scrollToId("top");
      return;
    }
    navigate("/");
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div className={`relative mx-auto max-w-7xl px-4 transition-all duration-500 sm:px-8 ${scrolled ? "pt-3" : "pt-5"}`}>
        <nav
          data-testid="main-nav"
          aria-label={lang === "ru" ? "Основная навигация" : "Primary navigation"}
          className="flex items-center justify-between gap-3 rounded-full border border-white/10 bg-slate-950/85 px-4 py-3 shadow-[0_8px_34px_rgba(0,0,0,0.22)] backdrop-blur-xl sm:px-7"
        >
          <a
            href={pathname === "/" ? "#top" : "/"}
            data-testid="nav-logo"
            onClick={(event) => {
              event.preventDefault();
              goHomeOrTop();
            }}
            className="flex items-center gap-3"
          >
            <span className="flex items-center gap-2.5" aria-label="MELNIK COMMERCIAL BROKERS">
              <img src="/melnik-monogram.png" alt="" className="h-11 w-auto object-contain" />
              <span className="flex flex-col leading-none">
                <span className="font-serif text-[0.98rem] tracking-[0.12em] text-[#F5F0E4]">MELNIK</span>
                <span className="mt-1 text-[0.42rem] font-semibold tracking-[0.25em] text-[#D4AF37]">COMMERCIAL BROKERS</span>
              </span>
            </span>
          </a>
          <div className="hidden items-center gap-5 xl:gap-7 lg:flex">
            {t("nav.links").map((link) => (
              <a
                key={link.id}
                href={pathname === "/" ? `#${link.id}` : `/#${link.id}`}
                data-testid={`nav-link-${link.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  goToSection(link.id);
                }}
                className="text-sm text-slate-400 transition-colors duration-300 hover:text-slate-100"
              >
                {link.label}
              </a>
            ))}
            <div className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={directionsOpen}
                data-testid="nav-directions-toggle"
                onClick={() => setDirectionsOpen((value) => !value)}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors duration-300 hover:text-slate-100"
              >
                {lang === "ru" ? "Направления" : "Directions"}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${directionsOpen ? "rotate-180" : ""}`} />
              </button>
              {directionsOpen && (
                <div role="menu" data-testid="nav-directions-menu" className="absolute right-0 top-[calc(100%+1rem)] w-56 rounded-2xl border border-white/10 bg-[#12141C]/95 p-2 shadow-2xl backdrop-blur-xl">
                  {directionLinks.map((link) => (
                    <a key={link.href} role="menuitem" href={link.href} onClick={() => setDirectionsOpen(false)} className="block rounded-xl px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-[#F3E5AB]">
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label={lang === "ru" ? "Открыть направления" : "Open directions"}
              aria-expanded={directionsOpen}
              data-testid="nav-directions-toggle-mobile"
              onClick={() => setDirectionsOpen((value) => !value)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-slate-300 transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3E5AB] lg:hidden"
            >
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${directionsOpen ? "rotate-180" : ""}`} />
            </button>
            <div data-testid="lang-switcher" className="flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1">
              {["ru", "en"].map((value) => (
                <button
                  key={value}
                  data-testid={`lang-${value}-button`}
                  onClick={() => setLang(value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${lang === value ? "bg-[#D4AF37] text-[#0A0B0E]" : "text-slate-400 hover:text-slate-100"}`}
                >
                  {value}
                </button>
              ))}
            </div>
            <button
              data-testid="nav-cta-button"
              onClick={() => goToSection("contact")}
              className="group hidden items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0A0B0E] transition-transform duration-300 hover:scale-[1.04] active:scale-95 sm:inline-flex"
            >
              {contactLabel}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </div>
        </nav>
        {directionsOpen && (
          <div role="menu" data-testid="nav-directions-menu-mobile" className="absolute left-4 right-4 top-[calc(100%+0.65rem)] rounded-2xl border border-white/10 bg-[#12141C]/95 p-2 shadow-2xl backdrop-blur-xl lg:hidden sm:left-8 sm:right-8">
            {directionLinks.map((link) => (
              <a key={link.href} role="menuitem" href={link.href} onClick={() => setDirectionsOpen(false)} className="block rounded-xl px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-[#F3E5AB]">
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </motion.header>
  );
};
